import { Box, ButtonGroup, Divider, IconButton, Popover } from '@mui/material'
import { getEnums } from '../../../utils/Common'
import { BuildingEditingTool } from '../../../models/Construction'
import { Fragment } from 'react/jsx-runtime'
import { cloneElement, useCallback, useState } from 'react'
import {
  AnimatedChevronIcon,
  AnnotationIcon,
  CommentIcon,
  DirectSelectIcon,
  FlipIcon,
  GridIcon,
  MarqueeIcon,
  MeasureIcon,
} from '../../Svgs'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '../../../store'
import { setSelectedTool } from '../../../actions/construction'
import GridSettingsComponent from '../../Tools/GridSettingsComponent'
import {
  ConstructionPage,
  ConstructionTab,
} from '../../../reducers/construction'

type EditingToolsElement = {
  icon: JSX.Element
  hasChevron: boolean
}

const EditingToolsElements: Record<BuildingEditingTool, EditingToolsElement> = {
  [BuildingEditingTool.DirectSelect]: {
    icon: <DirectSelectIcon />,
    hasChevron: false,
  },
  [BuildingEditingTool.Marquee]: {
    icon: <MarqueeIcon />,
    hasChevron: false,
  },
  [BuildingEditingTool.Measure]: {
    icon: <MeasureIcon />,
    hasChevron: false,
  },
  [BuildingEditingTool.Annotation]: {
    icon: <AnnotationIcon />,
    hasChevron: true,
  },
  [BuildingEditingTool.Flip]: {
    icon: <FlipIcon />,
    hasChevron: true,
  },
  [BuildingEditingTool.Grid]: {
    icon: <GridIcon />,
    hasChevron: true,
  },
  [BuildingEditingTool.Comment]: {
    icon: <CommentIcon />,
    hasChevron: false,
  },
}

const EditingTools = () => {
  const dispatch = useDispatch<AppDispatch>()

  const selectedTool = useSelector(
    (state: RootState) => state.construction.selectedTool,
  )

  const isArchitecturePage = useSelector(
    (state: RootState) =>
      state.construction.selectedConstructionPage ===
      ConstructionPage.Architecture,
  )
  const isEditorTab = useSelector(
    (state: RootState) =>
      state.construction.selectedConstructionTab === ConstructionTab.Editor,
  )

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const handleClose = () => {
    dispatch(setSelectedTool(null))
    setAnchorEl(null)
  }

  const handleToolClick = (event: any, value: BuildingEditingTool) => {
    dispatch(setSelectedTool(value))
    if ([BuildingEditingTool.Grid].includes(value)) {
      setAnchorEl(event.currentTarget)
    }
  }

  const renderToolOptions = useCallback(() => {
    switch (selectedTool) {
      case BuildingEditingTool.Grid:
        return <GridSettingsComponent />
    }

    return null
  }, [selectedTool])

  const isDisabled = useCallback(
    (selectedTool: BuildingEditingTool) => {
      switch (selectedTool) {
        case BuildingEditingTool.DirectSelect:
        case BuildingEditingTool.Grid:
        case BuildingEditingTool.Comment:
        case BuildingEditingTool.Measure:
          return false
        case BuildingEditingTool.Marquee:
          return !isEditorTab || !isArchitecturePage
        default:
          return true
      }
    },
    [isEditorTab, isArchitecturePage],
  )

  return (
    <>
      <Box
        sx={{
          borderRadius: '6px',
          background:
            'radial-gradient(73.19% 50% at 50% 50%, rgba(88, 88, 88, 0.30) 0%, rgba(204, 204, 204, 0.30) 100%)',
          boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
          pointerEvents: 'auto',
          padding: '6px',
        }}
      >
        <ButtonGroup
          size='small'
          sx={{
            background: '#FBFBFB',
            borderRadius: '4px',
            '& > .MuiIconButton-root[aria-selected="true"] + .MuiDivider-root, & > .MuiDivider-root:has(+ .MuiIconButton-root[aria-selected="true"])':
              {
                opacity: 0,
              },
          }}
        >
          {getEnums(BuildingEditingTool).map(([key, value], index) => {
            const isSelected = selectedTool === value
            const { icon, hasChevron } = EditingToolsElements[value]
            return (
              <Fragment key={key}>
                {index !== 0 && (
                  <Divider
                    flexItem
                    orientation='vertical'
                    variant='middle'
                    sx={{
                      backgroundColor: '#E4E4E4',
                      transition: '250ms all cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                    }}
                  />
                )}

                <IconButton
                  disabled={isDisabled(value)}
                  aria-selected={isSelected ? 'true' : 'false'}
                  sx={{
                    minWidth: '60px',
                    padding: '8px',
                    borderRadius: '4px',
                    backgroundColor: isSelected ? '#1705E5' : '#FBFBFB',
                    '&:hover': {
                      backgroundColor: isSelected ? '#1505CE' : '#E2E2E2',
                    },
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '8px',
                  }}
                  onClick={(event) => handleToolClick(event, value)}
                >
                  {cloneElement(icon, {
                    sx: {
                      color: isSelected ? '#F5F5F5' : '#04002E',
                      fontSize: '24px',
                    },
                  })}

                  {hasChevron && (
                    <AnimatedChevronIcon
                      open={isSelected}
                      reversed
                      sx={{
                        color: isSelected ? '#F5F5F5' : '#04002E',
                        fontSize: '12px',
                      }}
                    />
                  )}
                </IconButton>
              </Fragment>
            )
          })}
        </ButtonGroup>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            style: {
              boxShadow: '0px 4px 4px 0px #00000040',
              marginTop: '-1rem',
              borderRadius: '0.4rem',
              minWidth: 0,
              minHeight: 0,
            },
          },
        }}
      >
        {renderToolOptions()}
      </Popover>
    </>
  )
}

export default EditingTools
